// 답장 다듬기 AI 호출(api.md /ai/reply). 이름은 보내기 전에 마스킹하고 결과를 받은 뒤 되돌린다.
// 엔진이 꺼졌거나 8초 초과·오프라인·오류이면 null을 돌려주고, 호출자는 간단 모드로 넘어간다.
import { buildMask, unmask, type Maskable, type MaskMap } from './mask'

export interface Seg { t: string; hl: number }
export interface Variant {
  label: string
  bubbles: Seg[][] | null
  mail: { subject: Seg[]; body: Seg[][] } | null
  reasons: string[]
  missing: string[]
}
export interface ReplyResult { engine: string; variants: Variant[] }

export interface ReplyParams {
  people: Maskable[] // 받는 사람 먼저(= [P1])
  relation: string
  situation: string
  channel: 'chat' | 'mail'
  incoming: string
  draft: string
  uiLocale: 'ko' | 'en'
  replyLocale: 'ko' | 'en'
  recipientDo: string[]
  recipientDont: string[]
  recipientTip?: string // 성향 한 줄 요약(이름 없음) — 모델 맥락 보강
  variants?: number
}

function unmaskVariant(v: Variant, map: MaskMap): Variant {
  const us = (segs: Seg[]) => segs.map(s => ({ ...s, t: unmask(s.t, map) }))
  return {
    label: unmask(v.label || '', map),
    bubbles: v.bubbles ? v.bubbles.map(us) : null,
    mail: v.mail ? { subject: us(v.mail.subject), body: v.mail.body.map(us) } : null,
    reasons: (v.reasons || []).map(r => unmask(r, map)),
    missing: (v.missing || []).map(m => unmask(m, map)),
  }
}

export async function aiReply(p: ReplyParams): Promise<ReplyResult | null> {
  const { map, apply } = buildMask(p.people)
  const body = {
    ui_locale: p.uiLocale, reply_locale: p.replyLocale, channel: p.channel,
    relation: p.relation, situation: p.situation,
    recipient: { axes: {}, flags: [], tip: p.recipientTip || '', do: p.recipientDo, dont: p.recipientDont },
    thread: p.incoming.trim() ? [{ from: 'them', text: apply(p.incoming.trim()) }] : [],
    draft: apply(p.draft.trim()),
    variants: p.variants || 2, // 2개면 충분하고 토큰 잘림(→간단모드 폴백) 위험이 적다
  }
  const ctrl = new AbortController()
  // api.md: 동기 최대 8초. dev는 본체 Ollama가 느릴 수 있어 여유를 둔다(실제 AI 경로 확인용).
  const timer = setTimeout(() => ctrl.abort(), import.meta.env.DEV ? 30000 : 8000)
  try {
    const res = await fetch('/api/v1/ai/reply', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: ctrl.signal,
    })
    if (!res.ok) return null
    const data = await res.json()
    if (data.simple || !Array.isArray(data.variants) || !data.variants.length) return null
    return { engine: data.engine || 'unknown', variants: data.variants.map((v: Variant) => unmaskVariant(v, map)) }
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}
